import React, { useState } from "react";
import { Users, Copy, Check, X, UserPlus, LogOut as LeaveIcon } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useScope } from "../context/ScopeContext";
import { toast } from "sonner";

/**
 * Self-contained card to manage couple/household state. Renders inside
 * Configuración. Three UI states:
 *  - No household → invite generator + code-entry form
 *  - Pending invitation sent → show code + cancel
 *  - Active household → show partner + leave button
 */
export default function CoupleSection() {
  const { couple, hasHousehold, partner, reloadCouple } = useScope();
  const [acceptCode, setAcceptCode] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const pendingSent = couple?.sent_invitations?.[0];

  const createInvite = async () => {
    setBusy(true);
    try {
      const body = inviteEmail.trim() ? { partner_email: inviteEmail.trim() } : {};
      await api.post("/couple/invite", body);
      toast.success(
        inviteEmail.trim()
          ? "Invitación enviada · tu pareja la verá al entrar a FiPer"
          : "Código generado",
      );
      setInviteEmail("");
      await reloadCouple();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally {
      setBusy(false);
    }
  };

  const cancelInvite = async (id) => {
    if (!window.confirm("¿Cancelar la invitación?")) return;
    setBusy(true);
    try {
      await api.delete(`/couple/invite/${id}`);
      toast.success("Invitación cancelada");
      await reloadCouple();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally {
      setBusy(false);
    }
  };

  const acceptInvite = async (e) => {
    e.preventDefault();
    if (!acceptCode.trim()) return;
    setBusy(true);
    try {
      await api.post("/couple/accept", { code: acceptCode.trim() });
      toast.success("¡Vinculados como pareja!");
      setAcceptCode("");
      await reloadCouple();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Código inválido");
    } finally {
      setBusy(false);
    }
  };

  const leave = async () => {
    if (!window.confirm("¿Salir de la pareja? Mantendrás tus datos personales y tu pareja podrá seguir viendo los datos compartidos.")) return;
    setBusy(true);
    try {
      await api.post("/couple/leave", {});
      toast.success("Saliste de la pareja");
      await reloadCouple();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally {
      setBusy(false);
    }
  };

  const copyCode = (code) => {
    try {
      navigator.clipboard?.writeText(code);
      setCopied(true);
      toast.success("Código copiado");
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <section id="pareja" className="px-6 mt-8" data-testid="couple-section">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="font-display text-lg font-bold">Modo pareja</h3>
      </div>

      {hasHousehold ? (
        <div className="rounded-3xl bg-sage-50 border border-sage-100 p-5" data-testid="couple-active">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-2xl bg-sage-900 text-bone flex items-center justify-center font-display font-extrabold text-lg">
              {partner?.name?.[0]?.toUpperCase() || "P"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-sage-700">Pareja vinculada</p>
              <p className="font-display font-bold text-ink">{partner?.name || "Tu pareja"}</p>
              <p className="text-xs text-soft truncate">{partner?.email}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={leave}
            disabled={busy}
            data-testid="couple-leave-button"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-clay/10 text-clay font-bold py-3 rounded-2xl hover:bg-clay/15 disabled:opacity-60"
          >
            <LeaveIcon size={14} /> Salir de la pareja
          </button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="couple-inactive">
          {/* Send invite */}
          <div className="rounded-3xl bg-white border border-line shadow-card p-5">
            <div className="flex items-center gap-2 mb-1">
              <UserPlus size={16} className="text-sage-700" />
              <p className="font-display font-bold text-ink">Invita a tu pareja</p>
            </div>
            <p className="text-sm text-muted2">
              Genera un código y compártelo. Cuando lo introduzca, quedarán vinculados.
            </p>
            {pendingSent ? (
              <div className="mt-4 rounded-2xl bg-sage-50 border border-sage-100 p-4" data-testid="couple-pending-invite">
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-sage-700">Código activo</p>
                <div className="flex items-center justify-between gap-2 mt-1">
                  <p className="font-display font-extrabold text-2xl tabular text-ink" data-testid="couple-invite-code">{pendingSent.code}</p>
                  <button
                    type="button"
                    onClick={() => copyCode(pendingSent.code)}
                    data-testid="couple-copy-code"
                    aria-label="Copiar código"
                    className="h-9 w-9 rounded-full bg-white border border-line text-soft hover:text-ink flex items-center justify-center"
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                </div>
                <p className="text-xs text-soft mt-1">Comparte este código con tu pareja</p>
                <button
                  type="button"
                  onClick={() => cancelInvite(pendingSent.id)}
                  disabled={busy}
                  data-testid="couple-cancel-invite"
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 text-clay text-xs font-bold uppercase tracking-wider"
                >
                  <X size={12} /> Cancelar código
                </button>
              </div>
            ) : (
              <div className="mt-3 space-y-2">
                <input
                  type="email"
                  data-testid="couple-invite-email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="Email de tu pareja (opcional)"
                  className="w-full bg-white border border-line rounded-xl px-3 py-3 outline-none focus:border-sage-500 text-sm"
                  disabled={busy}
                />
                <p className="text-[11px] text-soft leading-snug">
                  Si pones su email, le aparecerá la invitación al entrar a FiPer (necesita estar registrado con ese email). Si no, solo comparte el código abajo.
                </p>
                <button
                  type="button"
                  onClick={createInvite}
                  disabled={busy}
                  data-testid="couple-create-invite"
                  className="w-full bg-sage-900 text-bone rounded-2xl py-3 font-bold hover:bg-sage-900/90 disabled:opacity-60"
                >
                  {busy ? "Generando…" : inviteEmail.trim() ? "Enviar invitación" : "Generar código"}
                </button>
              </div>
            )}
          </div>

          {/* Accept invite */}
          <div className="rounded-3xl bg-white border border-line shadow-card p-5">
            <div className="flex items-center gap-2 mb-1">
              <Users size={16} className="text-sage-700" />
              <p className="font-display font-bold text-ink">¿Te invitaron?</p>
            </div>
            <p className="text-sm text-muted2">
              Si tu pareja te compartió un código, ingrésalo aquí.
            </p>
            <form onSubmit={acceptInvite} className="mt-3 flex gap-2">
              <input
                data-testid="couple-accept-code-input"
                value={acceptCode}
                onChange={(e) => setAcceptCode(e.target.value.toUpperCase())}
                placeholder="FIPER-XXXXXX"
                className="flex-1 bg-bone border border-line rounded-xl px-3 py-3 outline-none focus:border-sage-500 font-display tabular tracking-wider uppercase"
              />
              <button
                type="submit"
                disabled={busy || !acceptCode.trim()}
                data-testid="couple-accept-button"
                className="bg-sage-900 text-bone rounded-xl px-4 font-bold disabled:opacity-60"
              >
                Aceptar
              </button>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
